import React from "react";
import { API } from "../API";
import { PatientTaskInstanceId } from "../messages/ObjectIds";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { AppModalProps } from "./Generic/AppModal/AppModal";
import { onTaskScheduleIsAlreadyTakenError } from "../utils/taskUtils";

interface Props {
  taskBroadcastId: PatientTaskInstanceId;
  api: API;
  goBackAndRefreshTaskBroadcasts: () => void;
  onAssignSuccess: () => void;
}

const initialModalState: AppModalProps = {
  type: "info",
  actions: [],
  title: "",
  isOpen: false,
};

export default function useAvailableTaskEngagement(props: Props) {
  const [modalState, setModalState] = React.useState<AppModalProps>(initialModalState);

  const handleCloseModal = () => {
    setModalState((prevState) => ({ ...prevState, isOpen: false }));
  };

  const handleCloseModalAndGoBack = () => {
    handleCloseModal();
    props.goBackAndRefreshTaskBroadcasts();
  };

  const setModalAsLoading = () => {
    setModalState((prevState) => ({ ...prevState, isLoading: true }));
  };

  const setModalAsError = (title: string, message?: string) => {
    setModalState({
      type: "error",
      isOpen: true,
      title: title,
      message: message,
      actions: [
        {
          key: "ok",
          variant: "soft",
          color: "black",
          children: translate(STRINGS.OK),
          onClick: handleCloseModalAndGoBack,
        },
      ],
    });
  };

  const setModalAsTaskRequested = () => {
    setModalState({
      type: "success",
      isOpen: true,
      title: translate(STRINGS.SUCCESSFULY_REQUESTED),
      message: `${translate(STRINGS.TO_SEE_THIS_TASK_GO_TO_ASSIGNED)} `,
      actions: [
        {
          key: "close-modal",
          variant: "soft",
          color: "primary",
          children: translate(STRINGS.CLOSE),
          onClick: handleCloseModalAndGoBack,
        },
      ],
    });
  };

  const promptRequestTaskBroadcast = () => {
    setModalState({
      isOpen: true,
      title: translate(STRINGS.TAKE_THIS_TASK),
      message: translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_TAKE_THIS_TASK),
      type: "info",
      actions: [
        {
          key: "yes",
          variant: "soft",
          color: "primary",
          children: translate(STRINGS.YES),
          onClick: () => {
            approveTaskbroadcast();
          },
        },
        {
          key: "no",
          variant: "link",
          color: "black",
          children: translate(STRINGS.I_HAVE_CHANGED_MY_MIND),
          onClick: handleCloseModal,
        },
      ],
    });
  };

  const approveTaskbroadcast = async () => {
    setModalAsLoading();

    return props.api
      .setTaskBroadcastAsApproved(props.taskBroadcastId)
      .then(() => {
        setModalAsTaskRequested();
        props.onAssignSuccess();
      })
      .catch((err) => {
        const title = translate(STRINGS.SCHEDULING_PROBLEM);
        let message: AppModalProps["message"];
        if (err.statusCode === 409) {
          message = onTaskScheduleIsAlreadyTakenError(err)
            ? `${translate(STRINGS.TASK_ALREADY_ASSIGNED)}`
            : `${translate(
                STRINGS.YOU_ALREADY_HAVE_A_VISIT_SCHEDULED_FOR_THAT_TIME_SLOT
              )}, ${translate(STRINGS.PLEASE_CONTACT_YOUR_COORDINATOR)}`;
        }

        setModalAsError(title, message);
      });
  };

  return {
    promptRequestTaskBroadcast: promptRequestTaskBroadcast,
    closeEngagementModal: handleCloseModal,
    engagementModalState: modalState,
  };
}
